import { earthquakeLayerConfig } from "@/constants/layerConfigAttributes";
import type { DraggableItemIdentifier } from "@/overlay/components/CommonParts/SortableList";
import CompleteThreshold from "@/overlay/components/CommonParts/Threshold/CompleteThreshold";
import { type ThresholdColor, transformColorThresholds } from "@/overlay/components/CommonParts/Threshold/types";
import { EarthquakeTimeDurationSelector } from "@/overlay/components/EarthquakeLayerUpdateForm/EarthquakeTimeDurationSelector";
import { ColorPickerInput, OpacityInput } from "@/overlay/components/LayerStackWindow/LayerFormElements";
import { FormField, InputFormField } from "@/overlay/components/form-field";
import { batchGroupBy } from "@/reducer/batchGroupBy";
import { t } from "@lingui/core/macro";
import type { EarthquakeLayer } from "@mm/metx-workbench.meteomatics.com";
import React from "react";

type updateLayerProps = {
  onChange: (propsValue: Partial<EarthquakeLayer>, isGrouped?: boolean) => void;
  layer: EarthquakeLayer;
};

export const EarthquakeLayerUpdateForm = (props: updateLayerProps) => {
  const thresholds = transformColorThresholds(props.layer.thresholds);

  function moveThresholdIdx(dragStart: DraggableItemIdentifier, dropTarget: DraggableItemIdentifier) {
    if (!thresholds.length) {
      return;
    }

    const copyOfThresholds = [...thresholds];
    const fromIdx = dragStart.index;

    let toIdx = dropTarget.index;
    if (toIdx >= copyOfThresholds.length) {
      toIdx = copyOfThresholds.length - 1;
    }
    if (toIdx < 0) {
      toIdx = 0;
    }

    const targetThreshold = copyOfThresholds.splice(fromIdx, 1)[0];
    copyOfThresholds.splice(toIdx, 0, targetThreshold);

    props.onChange({ thresholds: copyOfThresholds });
  }

  return (
    <>
      <OpacityInput
        layer={props.layer}
        change={props.onChange}
        onStart={() => batchGroupBy.start()}
        onEnd={() => batchGroupBy.end()}
      />
      <ColorPickerInput
        label={t`Color`}
        change={(color) =>
          props.onChange({
            element_color: color,
          })
        }
        value={props.layer.element_color || earthquakeLayerConfig.defaultElementColor}
      />
      <FormField label={t`Interval`}>
        <EarthquakeTimeDurationSelector
          onSelect={(timeDuration) => {
            props.onChange({
              custom_options: {
                ...(props.layer.custom_options || {}),
                time_duration: timeDuration,
              },
            });
          }}
          selectedItemKey={
            props.layer.custom_options?.time_duration ?? earthquakeLayerConfig.defaultEarthquakeLayerTimeDuration
          }
        />
      </FormField>
      <InputFormField
        label={t`Size`}
        type="range"
        onChange={(event) =>
          props.onChange({
            custom_options: {
              ...(props.layer.custom_options || {}),
              icon_size: Number(event.target.value),
            },
          })
        }
        {...earthquakeLayerConfig.elementSizeRange}
        value={Number(props.layer.custom_options?.icon_size ?? earthquakeLayerConfig.defaultElementSize)}
        onMouseDown={() => batchGroupBy.start()}
        onMouseUp={() => batchGroupBy.end()}
      />
      <FormField label={t`Clustering`}>
        <input
          type="checkbox"
          checked={props.layer.custom_options?.clustering ?? earthquakeLayerConfig.defaultEarthquakeLayerClustering}
          onChange={() =>
            props.onChange({
              custom_options: {
                ...(props.layer.custom_options || {}),
                clustering: !(
                  props.layer.custom_options?.clustering ?? earthquakeLayerConfig.defaultEarthquakeLayerClustering
                ),
              },
            })
          }
        />
      </FormField>

      <CompleteThreshold
        onMoveIndex={moveThresholdIdx}
        thresholds={thresholds}
        advancedThresholds={[]}
        updateThresholds={(newThresholds: ThresholdColor[]) => {
          props.onChange({ thresholds: newThresholds });
        }}
        updateAdvancedThresholds={() => {
          // Earthquakes currently support basic thresholds only.
        }}
      />
    </>
  );
};
